import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { TC_GECERSIZLIK_METNI, tcKimlikDogrula } from "@koclukpanel/contracts";
import { Alan, Buton } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";

/**
 * T.C. KİMLİK NUMARASINI DEĞİŞTİRME — AYRI EYLEM
 *
 * ================================================================
 * NEDEN PROFİL FORMUNUN İÇİNDE DEĞİL
 * ================================================================
 * Sunucuda ayrı bir uç ve ayrı bir izin var
 * (`kisi.tc_kimlik.degistir`, Emre'nin kararı 05.09.2026: *"sadece
 * yönetici değiştirebilsin"*). Alanı profil formuna koysaydık, izni
 * olmayan bir kullanıcı formu doldurup kaydeder ve **kaydın tamamı**
 * reddedilirdi — ad değişikliği de giderdi. Ayrı eylem, reddin ayrı
 * kalmasını sağlıyor.
 *
 * ================================================================
 * NEDEN TEK BİR BİLEŞEN
 * ================================================================
 * Aynı kural iki ekranda birden lazım (öğrenci dosyası ve personel
 * kartı). İki yere yazsaydık, doğrulama ya da maskeleme birinde
 * değişip ötekinde kalırdı — bu depoda bir günde defalarca ölçülen
 * hata. Kural burada, çağıran yalnızca kaydetme işlevini veriyor.
 *
 * ================================================================
 * HAM NUMARA TARAYICIYA HİÇ GELMİYOR
 * ================================================================
 * T.C. özel nitelikli kişisel veri (F-LOG-12). Bileşen **maskelenmiş**
 * dizeyi alıyor; maskeleme sunucuda yapılıyor ve ham numara yazıldıktan
 * sonra bir daha dışarı çıkmıyor.
 *
 * Buraya ham değeri gönderip burada maskeleseydik numara ağ trafiğinde,
 * tarayıcı belleğinde ve geliştirici araçlarında görünür olurdu —
 * ekranda maskeli görünmesi bunu değiştirmezdi. Maskeleme bir GÖSTERİM
 * tercihi değil, verinin nereye kadar gittiği kararı.
 *
 * Son iki hane bırakılıyor ki "hangi kayıt" sorusu cevaplanabilsin.
 */
export function TcKimlikDuzenle({
  mevcutMaskeli,
  kaydet,
  onKaydedildi,
}: {
  /** Sunucudan gelen MASKELİ dize (`•••••••••46`); numara yoksa `null`. */
  mevcutMaskeli: string | null;
  kaydet: (yeni: string | null) => Promise<unknown>;
  onKaydedildi?: () => void;
}): React.ReactElement {
  const [acik, setAcik] = useState(false);
  const [deger, setDeger] = useState("");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => kaydet(deger.trim() || null),
    onSuccess: () => {
      setAcik(false);
      setDeger("");
      setBilgi("T.C. kimlik numarası güncellendi.");
      onKaydedildi?.();
    },
  });

  /*
    İstemci doğrulaması KULLANICIYA ERKEN SÖYLEMEK için; karar
    sunucuda ve o da aynı kuralı uyguluyor. Mesaj NEDENE ÖZEL: "geçersiz"
    demek, on bir hane yazan kişiye numarayı DÜZELTTİRMEZ.
  */
  const ham = deger.trim();
  const sonuc = ham.length > 0 ? tcKimlikDogrula(ham) : null;
  const hata = sonuc && !sonuc.gecerli ? TC_GECERSIZLIK_METNI[sonuc.neden] : undefined;

  const sunucuHatasi =
    mut.error instanceof ApiHatasi ? mut.error.message : mut.error ? "Kaydedilemedi." : null;

  if (!acik) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-notr-600 text-sm">
          T.C. kimlik no: <strong className="text-notr-800">{mevcutMaskeli ?? "girilmemiş"}</strong>
        </span>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          {mevcutMaskeli ? "Değiştir" : "Ekle"}
        </Buton>
        {bilgi !== null && <span className="text-onay-700 text-sm">{bilgi}</span>}
      </div>
    );
  }

  return (
    <div className="border-notr-200 flex flex-col gap-3 rounded-lg border p-3">
      <Alan
        etiket="T.C. kimlik numarası"
        value={deger}
        onChange={(e) => setDeger(e.target.value)}
        inputMode="numeric"
        hata={hata}
        yardim="Boş bırakıp kaydederseniz numara silinir."
      />
      {sunucuHatasi !== null && (
        // Sunucunun mesajı AYNEN gösteriliyor: "bu numara başka bir
        // kayıtta kullanılıyor" gibi bir cevabı kendi kelimelerimizle
        // yeniden yazmak, sunucu kuralı değişince ekranı yanlış hâle
        // getirirdi.
        <p role="alert" className="text-hata-700 text-sm">
          {sunucuHatasi}
        </p>
      )}
      <div className="flex gap-2">
        <Buton
          disabled={hata !== undefined}
          yukleniyor={mut.isPending}
          yukleniyorMetni="Kaydediliyor"
          onClick={() => mut.mutate()}
        >
          Kaydet
        </Buton>
        <Buton
          gorunum="sessiz"
          onClick={() => {
            setAcik(false);
            setDeger("");
            mut.reset();
          }}
        >
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}
